import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import useApiHelper from "../hooks/useApiHelper";
import { DefaultResponseWithData } from "../models/system";
import {
  IInvitePartProTradeBuyerRequest,
  IPartProTradeBuyerLink,
} from "../models/partPro/IPartProTradeBuyer";
import apiConfig from "../utils/apiConfig";

type LinkResponse = DefaultResponseWithData<IPartProTradeBuyerLink>;

export default function useAccountPartProTrade(
  accountId?: string,
  enabled = true,
  componentName = "useAccountPartProTrade"
) {
  const { get, post, del } = useApiHelper(apiConfig(componentName));
  const queryClient = useQueryClient();

  // Under ["accounts", accountId] so a part category save refreshes it too
  const queryKey = ["accounts", accountId, "partpro-trade"];

  const link = useQuery({
    queryKey,
    queryFn: () => get<LinkResponse>(`/account/${accountId}/partpro-trade`),
    enabled: enabled && !!accountId,
    // A 400 here means "not enabled" or "not connected" - retrying will not change that
    retry: false,
  });

  // PartPro is the source of truth - seed the cache with what it returned
  const onSuccess = (res: LinkResponse) => queryClient.setQueryData(queryKey, res);

  const invite = useMutation({
    mutationFn: (req: IInvitePartProTradeBuyerRequest) =>
      post<IInvitePartProTradeBuyerRequest, LinkResponse>(`/account/${accountId}/partpro-trade/invite`, req),
    onSuccess,
  });

  const unlink = useMutation({
    mutationFn: () => del<LinkResponse>(`/account/${accountId}/partpro-trade`),
    onSuccess,
  });

  return { link, invite, unlink };
}
